import i18n from '../../i18n/i18n';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import {
  IChangePrimaryGuardianPayload,
  IChangePrimaryGuardianResponse,
  IGuardianPayload,
  ICreateGuardianResponse,
  IGetGuardiansParams,
  IGetGuardiansResponse,
  IGetGuardianTypesResponse,
  ILinkGuardianPayload,
  ILinkGuardianResponse,
  IRemoveGuardianRelationshipPayload,
  IRemoveGuardianRelationshipResponse,
  ISendGuardianInvitationPayload,
  ISendGuardianInvitationResponse,
  IUpdateGuardianPayload,
} from '../../interface/guardian.interface'; // Adjust path as needed
import { IErrorResponse } from '../../interface/main.interface'; // Assuming a generic error interface
import { guardianService } from '../../services/guardian'; // Adjust path as needed

/**
 * Async thunk to fetch a paginated list of guardians.
 */
export const getGuardians = createAsyncThunk<
  IGetGuardiansResponse,
  IGetGuardiansParams,
  { rejectValue: IErrorResponse }
>('guardian/get-all', async (params, { rejectWithValue }) => {
  try {
    const response = await guardianService.getGuardians(params);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue(error.response?.data || { message: i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred') });
    }
    return rejectWithValue({ message: err.message });
  }
});

/**
 * Async thunk to change the primary guardian for a student.
 */
export const changePrimaryGuardian = createAsyncThunk<
  IChangePrimaryGuardianResponse,
  IChangePrimaryGuardianPayload,
  { rejectValue: IErrorResponse }
>('guardian/change-primary', async (payload, { rejectWithValue }) => {
  try {
    const response = await guardianService.changePrimaryGuardian(payload);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue(error.response?.data || { message: i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred') });
    }
    return rejectWithValue({ message: err.message });
  }
});

/**
 * Async thunk to create a new guardian and link them to a student.
 */
export const createGuardian = createAsyncThunk<
  ICreateGuardianResponse,
  IGuardianPayload,
  { rejectValue: IErrorResponse }
>('guardian/create', async (payload, { rejectWithValue }) => {
  try {
    const response = await guardianService.createGuardian(payload);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue(error.response?.data || { message: i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred') });
    }
    return rejectWithValue({ message: err.message });
  }
});

/**
 * Async thunk to update a guardian.
 */
export const updateGuardian = createAsyncThunk<
  { message: string },
  { id: string; payload: IUpdateGuardianPayload },
  { rejectValue: IErrorResponse }
>('guardian/update', async ({ id, payload }, { rejectWithValue }) => {
  try {
    const response = await guardianService.updateGuardian(id, payload);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue(error.response?.data || { message: i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred') });
    }
    return rejectWithValue({ message: err.message });
  }
});

/**
 * Async thunk to remove the relationship between a guardian and a student.
 */
export const removeGuardian = createAsyncThunk<
  IRemoveGuardianRelationshipResponse,
  IRemoveGuardianRelationshipPayload,
  { rejectValue: IErrorResponse }
>('guardian/remove-relationship', async (payload, { rejectWithValue }) => {
  try {
    const response = await guardianService.removeGuardian(payload);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue(error.response?.data || { message: i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred') });
    }
    return rejectWithValue({ message: err.message });
  }
});

/**
 * Async thunk to remove the relationship between a guardian and a student.
 */
export const linkGuardianToStudent = createAsyncThunk<
  ILinkGuardianResponse,
  ILinkGuardianPayload,
  { rejectValue: IErrorResponse }
>('guardian/add-relationship', async (payload, { rejectWithValue }) => {
  try {
    const response = await guardianService.linkGuardianToStudent(payload);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue(error.response?.data || { message: i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred') });
    }
    return rejectWithValue({ message: err.message });
  }
});

/**
 * Async thunk to fetch the list of guardian relationship types.
 */
export const getGuardianTypes = createAsyncThunk<
  IGetGuardianTypesResponse,
  void,
  { rejectValue: IErrorResponse }
>('guardian/get-types', async (_, { rejectWithValue }) => {
  try {
    const response = await guardianService.getGuardianTypes();
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue(error.response?.data || { message: i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred') });
    }
    return rejectWithValue({ message: err.message });
  }
});

/**
 * Async thunk to delete a guardian.
 */
export const deleteGuardian = createAsyncThunk<
  { message: string },
  string,
  { rejectValue: IErrorResponse }
>('guardian/delete', async (id, { rejectWithValue }) => {
  try {
    const response = await guardianService.deleteGuardian(id);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue(error.response?.data || { message: i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred') });
    }
    return rejectWithValue({ message: err.message });
  }
});

/**
 * Async thunk to send (or resend) invitations to the selected guardians.
 * The backend endpoint is idempotent for resends — invoking it for an
 * already-invited guardian simply re-sends the invitation.
 */
export const sendGuardianInvitation = createAsyncThunk<
  ISendGuardianInvitationResponse,
  ISendGuardianInvitationPayload,
  { rejectValue: IErrorResponse }
>('guardian/send-invitation', async (payload, { rejectWithValue }) => {
  try {
    const response = await guardianService.sendInvitation(payload);
    return response.data;
  } catch (err: any) {
    // The axios response interceptor rejects with the backend response body
    // itself (not an AxiosError), so read fields off the body directly.
    return rejectWithValue(
      err?.message ? { message: err.message } : { message: i18n.t('toast.failed_to_send_invitations', 'Failed to send invitations') },
    );
  }
});

export const guardianActions = {
  getGuardians,
  changePrimaryGuardian,
  createGuardian,
  updateGuardian,
  deleteGuardian,
  removeGuardian,
  getGuardianTypes,
  linkGuardianToStudent,
  sendGuardianInvitation,
};
